/**
 * JaarurenStandCard — pace-grafiek voor jaarurennorm-medewerkers.
 *
 * Zelfde beeld als de Kwartaaldoel-kaart op /cijfers: groene cumulatieve lijn
 * (gewerkt + verlof), grijze pace-lijn (naar rato verstreken jaarnorm) en een
 * stippellijn-projectie naar het jaareinde. Data uit
 * GET /api/workforce/jaaruren-stand (jaaruren-stand.ts primitief).
 */

import { useEffect, useState } from 'react';
import {
  CartesianGrid,
  ComposedChart,
  Line,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { supabase } from '../../lib/supabase';
import type { JaarurenStand } from '../../lib/jaaruren-stand';

interface JaarurenStandCardProps {
  staffId: string;
  year: number;
}

interface ChartRow {
  label: string;
  gewerkt?: number;
  norm?: number;
  verwacht?: number;
}

const SERIES_LABELS: Record<string, string> = {
  gewerkt: 'Gewerkt + verlof',
  norm: 'Norm t/m nu',
  verwacht: 'Verwacht',
};

function shortLabel(date: string): string {
  const [, m, d] = date.split('-');
  return `${parseInt(d!, 10)}/${parseInt(m!, 10)}`;
}

function formatUren(n: number): string {
  return n % 1 === 0 ? String(n) : n.toFixed(1).replace('.', ',');
}

export function JaarurenStandCard({ staffId, year }: JaarurenStandCardProps) {
  const [stand, setStand] = useState<(JaarurenStand & { name: string }) | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      setError(null);
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (!session?.access_token) throw new Error('Niet ingelogd');
        const res = await fetch(`/api/workforce/jaaruren-stand?staff_id=${staffId}&year=${year}`, {
          headers: { Authorization: `Bearer ${session.access_token}` },
        });
        if (!res.ok) {
          const body = await res.json().catch(() => null);
          throw new Error(body?.error || 'Jaaruren-stand laden mislukt');
        }
        const data = await res.json();
        if (!cancelled) setStand(data);
      } catch (e) {
        if (!cancelled) setError((e as Error).message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, [staffId, year]);

  if (loading) {
    return (
      <div className="bg-white border border-gray-200 rounded-xl p-5 h-52 flex items-center justify-center text-sm text-gray-400">
        Jaaruren laden…
      </div>
    );
  }
  if (error || !stand) {
    return (
      <div className="bg-white border border-gray-200 rounded-xl p-5 text-sm text-red-600">
        {error || 'Jaaruren-stand niet beschikbaar'}
      </div>
    );
  }

  const todayLabel = stand.weekly.length > 0 ? shortLabel(stand.weekly[stand.weekly.length - 1]!.date) : null;
  const rows: ChartRow[] = stand.weekly.map((p, i) => ({
    label: shortLabel(p.date),
    gewerkt: p.gewerkt,
    norm: p.norm,
    // Projectie start op het laatste meetpunt zodat de stippellijn aansluit
    ...(i === stand.weekly.length - 1 ? { verwacht: p.gewerkt } : {}),
  }));
  rows.push({ label: '31/12', norm: stand.normProRata, verwacht: stand.verwachtJaareinde });

  const saldoPositief = stand.saldo >= 0;
  const capGebruik = stand.saldo > 0 ? Math.min(100, (stand.saldo / stand.cap) * 100) : 0;
  const capBijnaVol = stand.saldo > 0.8 * stand.cap;

  return (
    <div className="bg-white border border-gray-200 rounded-xl p-5">
      <div className="flex items-start justify-between mb-4">
        <div>
          <h3 className="text-sm font-medium text-main">Jaaruren {year}</h3>
          <p className="text-xs text-gray-400">
            Gewerkt + verlof t.o.v. de naar rato verstreken jaarnorm ({formatUren(stand.jaarNorm)} uur/jaar).
            Streepje = waar je vandaag hoort te staan; licht = verwacht jaareinde in dit tempo.
          </p>
        </div>
        <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${saldoPositief ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-600'}`}>
          {saldoPositief ? '+' : ''}{formatUren(stand.saldo)} uur
        </span>
      </div>

      <div className="grid md:grid-cols-2 gap-4 items-center">
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
            <div>
              <p className="text-[10px] text-muted uppercase tracking-wider">Gewerkt + verlof</p>
              <p className="font-semibold text-main">{formatUren(stand.totaal)} uur</p>
            </div>
            <div>
              <p className="text-[10px] text-muted uppercase tracking-wider">Norm t/m nu</p>
              <p className="font-semibold text-main">{formatUren(stand.normToDate)} uur</p>
            </div>
            <div>
              <p className="text-[10px] text-muted uppercase tracking-wider">Verwacht jaareinde</p>
              <p className="font-semibold text-main">{formatUren(stand.verwachtJaareinde)} / {formatUren(stand.normProRata)} uur</p>
            </div>
            <div>
              <p className="text-[10px] text-muted uppercase tracking-wider">Waarvan verlof/ziekte</p>
              <p className="font-semibold text-main">{formatUren(stand.verlof)} uur</p>
            </div>
          </div>

          {/* Spaaruren-cap (cao art 8 B-deel: max 100u naar rato) */}
          <div>
            <div className="flex justify-between text-[10px] text-muted mb-1">
              <span>Spaaruren-cap</span>
              <span className={capBijnaVol ? 'text-amber-600 font-semibold' : ''}>
                {stand.saldo > 0 ? `${formatUren(stand.saldo)} / ${formatUren(stand.cap)} uur` : `max ${formatUren(stand.cap)} uur`}
              </span>
            </div>
            <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
              <div
                className={`h-full rounded-full transition-all ${capBijnaVol ? 'bg-amber-500' : 'bg-emerald-500'}`}
                style={{ width: `${capGebruik}%` }}
              />
            </div>
            {capBijnaVol && (
              <p className="text-[10px] text-amber-600 mt-1">
                Saldo nadert de cap van {formatUren(stand.cap)} uur — plan afbouw in (cao art 8 B-deel).
              </p>
            )}
          </div>
        </div>

        <ResponsiveContainer width="100%" height={190}>
          <ComposedChart data={rows} margin={{ top: 8, right: 8, bottom: 0, left: 8 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" vertical={false} />
            <XAxis
              dataKey="label"
              tick={{ fill: '#6b7280', fontSize: 10 }}
              tickLine={false}
              axisLine={{ stroke: '#e5e7eb' }}
              minTickGap={16}
            />
            <YAxis
              tick={{ fill: '#6b7280', fontSize: 10 }}
              tickLine={false}
              axisLine={false}
              width={46}
              tickFormatter={(v: number) => `${v}u`}
            />
            <Tooltip
              contentStyle={{ backgroundColor: '#ffffff', border: '1px solid #e5e7eb', borderRadius: 8, fontSize: 12 }}
              formatter={(value: number, name: string) => [`${formatUren(value)} uur`, SERIES_LABELS[name] ?? name]}
            />
            {todayLabel && (
              <ReferenceLine
                x={todayLabel}
                stroke="#9ca3af"
                strokeDasharray="4 3"
                label={{ value: 'vandaag', position: 'top', fill: '#9ca3af', fontSize: 10 }}
              />
            )}
            <Line type="linear" dataKey="norm" stroke="#9ca3af" strokeWidth={1.5} dot={false} isAnimationActive={false} />
            <Line
              type="monotone"
              dataKey="gewerkt"
              stroke="#10b981"
              strokeWidth={2}
              dot={false}
              isAnimationActive={false}
              connectNulls={false}
            />
            <Line
              type="linear"
              dataKey="verwacht"
              stroke="#10b981"
              strokeWidth={1.5}
              strokeDasharray="5 4"
              dot={false}
              isAnimationActive={false}
              connectNulls={false}
            />
          </ComposedChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
